/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { defineMessages } from 'react-intl-next';
import { useState, useEffect } from 'react';

import { jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { FlagsProvider, useFlags } from '@atlaskit/flag';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';

import type { AIPlugin } from '@atlassian/editor-plugin-ai';

export const i18n = defineMessages({
	title: {
		id: 'experiment-improve-writing-on-paste.improve-writing-flag.title',
		defaultMessage: 'Use AI by highlighting text',
		description: 'Popup title to bring awareness to the Improve Writing feature.',
	},
	description: {
		id: 'experiment-improve-writing-on-paste.improve-writing-flag.description',
		defaultMessage: 'Edit with AI by highlighting text on the page and selecting Improve writing.',
		description:
			'Context explaining that users can use AI to edit their writing by highlighting text and selecting the Improve writing option.',
	},
});

type PresetAPI = PublicPluginAPI<[AIPlugin]>;

interface ImproveWritingFlagProps {
	editorAPI: PresetAPI;
}

interface AIExperienceState {
	aiPromptActionCompleted?: {
		actionSource?: ActionSource;
		actionType?: ActionType;
		prompt?: string;
	};
}

export type ActionType = 'insert' | 'replace' | 'discard' | 'second-paste';
export type ActionSource = 'preview-screen' | 'discard-screen' | 'auto-paste';

export interface PromptActionCompleted {
	actionSource: ActionSource;
	actionType: ActionType;
	prompt: string;
}

export const ImproveWritingFlag = ({ editorAPI }: ImproveWritingFlagProps) => {
	const [flagShownFirstPaste, setFlagShownFirstPaste] = useState(false);
	const [flagShownSecondPaste, setFlagShownSecondPaste] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlags();

	const [promptAction, setPromptAction] = useState<PromptActionCompleted | null>(null);

	// Listen to the AI prompt action plugin state
	// `aiPromptActionCompleted` is part of AIPlugin EditorPluginAISharedState
	const { actionSource, actionType, prompt } = useSharedPluginStateWithSelector(
		editorAPI,
		['aiExperience'],
		({ aiExperienceState }: { aiExperienceState?: AIExperienceState }) => {
			return {
				actionSource: aiExperienceState?.aiPromptActionCompleted?.actionSource,
				actionType: aiExperienceState?.aiPromptActionCompleted?.actionType,
				prompt: aiExperienceState?.aiPromptActionCompleted?.prompt,
			};
		},
	);

	// Update state when plugin state changes to display flag
	useEffect(() => {
		if (actionType && prompt === 'improve-writing') {
			setPromptAction({
				actionSource: actionSource as ActionSource,
				actionType: actionType as ActionType,
				prompt,
			});
		}
	}, [actionSource, actionType, prompt]);

	if (
		promptAction &&
		(!flagShownFirstPaste || (!flagShownSecondPaste && promptAction.actionType === 'second-paste'))
	) {
		if (promptAction.actionType === 'second-paste') {
			setFlagShownSecondPaste(true);
		}
		// edge case: first paste occurs without a flag trigger (ex: navigate away before finishing experience)
		//   -> second paste causes infinite display of first paste flag
		// to prevent this, we always setFlagShownFirstPaste -> true
		setFlagShownFirstPaste(true);

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				action: 'viewed',
				name: 'improveWritingFlag',
				attributes: {
					actionSource: promptAction?.actionSource,
					actionType: promptAction?.actionType,
					prompt: promptAction?.prompt,
					source: 'improveWritingOnPaste',
				},
			},
		}).fire();

		showFlag({
			id: 'improve-writing-flag',
			title: i18n.title.defaultMessage,
			description: i18n.description.defaultMessage,
			appearance: 'normal',
			icon: <StatusInformationIcon label="Info" color={token('color.icon.information')} />,
			onDismissed: () => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'improveWritingFlag',
						source: 'improveWritingOnPaste',
						attributes: {
							actionSource: promptAction?.actionSource,
							actionType: promptAction?.actionType,
							prompt: promptAction?.prompt,
							buttonType: 'dismissButton',
						},
					},
				}).fire();
			},
		});
	}

	return null;
};

export const ImproveWritingFlagContainer = ({ editorAPI }: ImproveWritingFlagProps) => {
	return (
		<FlagsProvider>
			<ImproveWritingFlag editorAPI={editorAPI} />
		</FlagsProvider>
	);
};
